import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ParentNav } from '../../components/shared/BottomNav';
import { subscribeToWeeks, formatCurrency, getCurrentWeekId, getWeekTransactions, subscribeToGoalHistory } from '../../services/firebase';
import { format } from 'date-fns';

const ParentHistory = () => {
  const { family, refreshKey } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goalHistory, setGoalHistory] = useState([]);

  const studentFirst = family?.studentName?.split(' ')[0] || 'Student';

  useEffect(() => {
    if (!family?.id) return;
    setLoading(true);
    setWeeks([]);
    const unsub = subscribeToWeeks(family.id, (w) => {
      setWeeks(w.filter(week => week.id <= getCurrentWeekId()));
      setLoading(false);
    });
    return unsub;
  }, [family?.id, refreshKey]);

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToGoalHistory(family.id, setGoalHistory);
    return unsub;
  }, [family?.id]);

  const totalSpent = weeks.reduce((s, w) => s + (w.spent || 0), 0);
  // Only count planned spend from open (unclosed) weeks — closed weeks have already resolved planned items
  const totalPlanned = weeks.filter(w => !w.closedOut).reduce((s, w) => s + (w.plannedSpend || 0), 0);
  const totalSaved = weeks.reduce((s, w) => s + (w.saved || 0), 0);
  const totalMatch = weeks.reduce((s, w) => s + (w.matchEarned || 0), 0);
  const closedWeeks = weeks.filter(w => w.closedOut);
  const avgSaved = closedWeeks.length > 0 ? totalSaved / closedWeeks.length : 0;
  const hasPlanned = totalPlanned > 0;
  const purchasedGoalsTotal = goalHistory
    .filter(g => g.status === 'completed' && g.spentFromSavings)
    .reduce((s, g) => s + (g.targetAmount || 0), 0);
  const netSavings = Math.max(0, Math.round((totalSaved - purchasedGoalsTotal) * 100) / 100);
  const hasPurchasedGoals = purchasedGoalsTotal > 0;

  return (
    <div className="screen">
      <ParentNav />
      <div className="page-header">
        <h1 className="page-title">History</h1>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)' }}>
          Last {weeks.length} weeks
        </span>
      </div>

      {/* Summary hero card */}
      <div style={{
        background: 'var(--color-primary-dark, #0d5c3a)',
        borderRadius: 'var(--radius-xl)',
        padding: '22px 20px',
        marginBottom: 24,
      }}>
        {/* Spending section */}
        <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)',
          letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>
          Spending
        </div>
        <div style={{ display: 'flex' }}>
          <HeroStat label="Spent" value={formatCurrency(totalSpent)} amber />
          {hasPlanned && <HeroStat label="Planned" value={formatCurrency(totalPlanned)} amber divider />}
          {hasPlanned && <HeroStat label="Projected" value={formatCurrency(totalSpent + totalPlanned)} divider summary />}
        </div>

        {/* Divider */}
        <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.25)', margin: '20px 0' }} />

        {/* Savings section */}
        <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)',
          letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>
          Savings
        </div>
        <div style={{ display: 'flex' }}>
          <HeroStat label={`${studentFirst}'s`} value={formatCurrency(hasPurchasedGoals ? netSavings : totalSaved)} />
          {family?.match?.enabled && <HeroStat label="Match" value={formatCurrency(totalMatch)} divider />}
          {family?.match?.enabled && <HeroStat label="Total" value={formatCurrency((hasPurchasedGoals ? netSavings : totalSaved) + totalMatch)} divider summary />}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      ) : weeks.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: 'var(--color-text-3)' }}>No history yet — check back after {studentFirst}'s first week.</p>
        </div>
      ) : (
        <div className="stack stack-3">
          {weeks.map(week => (
            <WeekCard key={week.id} week={week} allowance={family?.allowance?.amount || 0}
              matchEnabled={family?.match?.enabled} familyId={family?.id} />
          ))}
        </div>
      )}
    </div>
  );
};

const CATEGORY_EMOJI = {
  food: '🍕', transport: '🚗', groceries: '🛒', entertainment: '🎬',
  clothing: '👕', health: '💊', books: '📚', other: '📦',
  gift: '🎁', job: '💼', refund: '↩️',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const WeekCard = ({ week, allowance, matchEnabled, familyId }) => {
  const [expanded, setExpanded] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txLoaded, setTxLoaded] = useState(false);
  const spent = week.spent || 0;
  const planned = week.plannedSpend || 0;
  const saved = week.saved || 0;
  const match = week.matchEarned || 0;
  const bonusIncome = week.bonusIncome || 0;

  const handleToggle = async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && !txLoaded && familyId) {
      setTxLoading(true);
      try {
        const txs = await getWeekTransactions(familyId, week.id);
        setTransactions(txs.filter(t => {
          if (t.type === 'deleted' || t.type === 'converted') return false;
          // For closed weeks, exclude any remaining planned items — they were rolled out or are stale
          if (week.closedOut && t.type === 'planned') return false;
          return true;
        }).sort((a, b) => {
          const ta = a.createdAt?.seconds || 0;
          const tb = b.createdAt?.seconds || 0;
          return tb - ta;
        }));
        setTxLoaded(true);
      } catch (err) {
        console.error('Failed to load transactions:', err);
      } finally {
        setTxLoading(false);
      }
    }
  };
  const effectiveAllowance = (week.allowanceAmount || allowance) + bonusIncome;
  const spentPct = effectiveAllowance > 0 ? Math.min(100, (spent / effectiveAllowance) * 100) : 0;
  const savedPct = effectiveAllowance > 0 ? Math.min(100, (saved / effectiveAllowance) * 100) : 0;
  const plannedPct = effectiveAllowance > 0 ? Math.min(100, (planned / effectiveAllowance) * 100) : 0;

  const startDate = week.startDate?.toDate ? week.startDate.toDate() : new Date(week.startDate);
  const endDate = week.endDate?.toDate ? week.endDate.toDate() : new Date(week.endDate);

  // Planned vs actual insight
  const hadPlanned = planned > 0 || (week.closedOut && spent > 0);
  const variance = spent - (week.baseAllowance || allowance);

  return (
    <div className="card" style={{ cursor: 'pointer' }} onClick={handleToggle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 600 }}>
            {format(startDate, 'MMM d')} – {format(endDate, 'MMM d')}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 2 }}>
            Week {week.id?.split('-')[1]}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {week.closedOut
            ? <span className="badge badge--green">Closed</span>
            : <span className="badge badge--amber">Open</span>
          }
          <span style={{ color: 'var(--color-text-3)', fontSize: '0.875rem' }}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {/* Mini progress bar — 3 segments if planned exists */}
      <div style={{ height: 6, background: 'var(--color-surface-2)',
        borderRadius: 'var(--radius-full)', overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: `${spentPct}%`, background: 'var(--color-accent)' }} />
        {planned > 0 && (
          <div style={{ width: `${plannedPct}%`,
            background: 'repeating-linear-gradient(45deg,#F4A261,#F4A261 3px,#FFDDD2 3px,#FFDDD2 6px)' }} />
        )}
        <div style={{ width: `${savedPct}%`, background: 'var(--color-primary)' }} />
      </div>

      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16,
          borderTop: '1px solid var(--color-border)' }} className="animate-in">
          <div className="stack stack-3">
            <DetailRow label="Weekly allowance" value={formatCurrency(week.baseAllowance || allowance)} />
            {bonusIncome > 0 && (
              <DetailRow label="🎁 Bonus income" value={`+${formatCurrency(bonusIncome)}`}
                color="var(--color-primary)" />
            )}
            {(week.carryoverFromPrev !== undefined && week.carryoverFromPrev !== 0) && (
              <DetailRow
                label="Rolled forward"
                value={`${week.carryoverFromPrev >= 0 ? '+' : ''}${formatCurrency(week.carryoverFromPrev)}`}
                color={week.carryoverFromPrev >= 0 ? 'var(--color-primary)' : 'var(--color-error)'}
              />
            )}
            <DetailRow label="Spent" value={formatCurrency(spent)} color="var(--color-accent)" />
            {planned > 0 && !week.closedOut && (
              <DetailRow label="Still planned" value={formatCurrency(planned)}
                color="#C45E1A" />
            )}
            {/* Green savings summary for closed weeks */}
            {week.closedOut && saved > 0 ? (
              <div style={{
                background: 'var(--color-primary-pale)',
                border: '1px solid var(--color-primary-light)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                marginTop: 4,
              }}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--color-primary)',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Saved this week
                </div>
                <DetailRow label="Saved" value={formatCurrency(saved)} color="var(--color-primary)" />
                {matchEnabled && match > 0 && (
                  <DetailRow label="🎯 Match earned" value={formatCurrency(match)} color="var(--color-primary)" />
                )}
                {week.carryover !== undefined && week.carryover > 0 && (
                  <DetailRow label="Rolled to next week" value={`+${formatCurrency(week.carryover)}`} color="var(--color-primary)" />
                )}
              </div>
            ) : (
              <>
                <DetailRow label="Saved" value={formatCurrency(saved)} color="var(--color-primary)" />
                <DetailRow label="Remaining" value={formatCurrency(Math.max(0, effectiveAllowance - spent - saved - (week.closedOut ? 0 : planned)))} />
                {matchEnabled && match > 0 && (
                  <DetailRow label="Match earned" value={formatCurrency(match)} color="var(--color-primary)" />
                )}
              </>
            )}
            {!week.closedOut && (
              <DetailRow label="Remaining" value={formatCurrency(Math.max(0, effectiveAllowance - spent - saved - planned))} />
            )}
            {/* Transaction list */}
            {txLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                <div className="spinner" />
              </div>
            )}
            {txLoaded && transactions.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-3)',
                  letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
                  Transactions
                </div>
                <div className="stack stack-2">
                  {transactions.map(tx => {
                    const emoji = CATEGORY_EMOJI[tx.category] || '📦';
                    const dayStr = tx.spendDate !== undefined ? DAYS[tx.spendDate] : '';
                    const isIncome = tx.type === 'income';
                    const isPlanned = tx.type === 'planned';
                    const color = isIncome ? 'var(--color-primary)' : isPlanned ? 'var(--color-accent)' : 'var(--color-error)';
                    return (
                      <div key={tx.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 0', borderBottom: '1px solid var(--color-border)',
                      }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8,
                          background: 'var(--color-surface-2)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, flexShrink: 0 }}>
                          {emoji}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tx.note || tx.category}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 1 }}>
                            {tx.category}{dayStr ? ` · ${dayStr}` : ''}
                            {isPlanned && <span style={{ color: 'var(--color-accent)', fontWeight: 600 }}> · planned</span>}
                            {isIncome && <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}> · income</span>}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 700, color, flexShrink: 0 }}>
                          {isIncome ? '+' : '-'}{formatCurrency(tx.amount)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {txLoaded && transactions.length === 0 && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)',
                fontSize: '0.875rem', color: 'var(--color-text-3)', textAlign: 'center' }}>
                No transactions logged this week.
              </div>
            )}
            {week.closedOut && week.carryover < 0 && (
              <div style={{ marginTop: 4, padding: '10px 12px',
                background: 'var(--color-error-light)', borderRadius: 'var(--radius-md)' }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-error)', margin: 0 }}>
                  ⚠️ Overspent by {formatCurrency(Math.abs(week.carryover))} — carried into next week.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const HeroStat = ({ label, value, divider, amber, summary }) => (
  <div style={{
    flex: 1,
    paddingLeft: divider ? 16 : 0,
    paddingRight: 16,
    borderLeft: divider ? '0.5px solid rgba(255,255,255,0.2)' : 'none',
  }}>
    <div style={{ fontSize: '0.625rem',
      color: summary ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)',
      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>
      {label}
    </div>
    <div style={{ fontSize: '1.25rem',
      fontWeight: summary ? 700 : 600,
      color: amber ? '#F4A261' : 'white',
      fontFamily: 'var(--font-display)' }}>
      {value}
    </div>
  </div>
);

const MiniStat = ({ label, value, color, subtitle }) => (
  <div className="card" style={{ textAlign: 'center', padding: '14px 10px' }}>
    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem',
      fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-3)', marginTop: 4 }}>{label}</div>
    {subtitle && (
      <div style={{ fontSize: '0.65rem', color: 'var(--color-text-3)',
        marginTop: 2, opacity: 0.75 }}>{subtitle}</div>
    )}
  </div>
);

const DetailRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
    <span style={{ color: 'var(--color-text-2)' }}>{label}</span>
    <span style={{ fontWeight: 600, color: color || 'var(--color-text)' }}>{value}</span>
  </div>
);

export default ParentHistory;
